import { LOG_PREFIX, readEnv } from './env';
import type { LDDevServerBootstrap } from './devServerBootstrap';
import type { ProviderConfig } from 'launchdarkly-react-client-sdk';

/**
 * The `LDProvider` configuration for one render.
 *
 * Two shapes come out of here: the ordinary hosted-LaunchDarkly one every
 * deployment has always used, and - only on a stack that rendered its own
 * LaunchDarkly CLI Dev Server AND whose server render managed to read it - one
 * driven entirely by the flag set that render already fetched.
 *
 * # Why this is code rather than configuration
 *
 * The JS SDK takes its service endpoints and its bootstrap as `LDOptions`
 * fields only; it reads no environment variable of its own. So the branch has
 * to live somewhere, and it lives here rather than inline in `_app.page.tsx` so
 * it can be tested without rendering the whole app.
 *
 * # Per render, not per process
 *
 * This used to be called once at module scope, through
 * `withLDProvider(ldProviderConfig())`. It cannot be: a Dev Server bootstrap is
 * per-REQUEST data, and `withLDProvider` freezes its config the first time the
 * module is evaluated. `_app.page.tsx` therefore renders `<LDProvider>` as a
 * component and calls this with the bootstrap for the render in hand.
 *
 * # Why the bootstrap - not an environment variable - decides the branch
 *
 * `LD_DEV_SERVER_URL`/`LD_DEV_SERVER_PROJECT_KEY` are referenced only by
 * `utils/launchdarkly/devServerBootstrapFetch`, which only
 * `src/pages/_document.page.js` imports, so neither name is compiled into the
 * browser bundle. The browser has no use for the address (it is a ClusterIP
 * name it cannot resolve) and no use for the project key except as the
 * credential that travels with a bootstrap - so both arrive here inside the
 * bootstrap or not at all.
 *
 * Deciding the branch on the bootstrap is also what keeps it safe. Pointing the
 * SDK at a project key with no bootstrap would send a browser to
 * HOSTED LaunchDarkly holding a credential that cannot authenticate there -
 * `LDInvalidEnvironmentIdError` on every page load. The only state in which the
 * project key is a usable credential is the one where nothing is fetched with
 * it, and that state is exactly "a bootstrap arrived".
 */

export function ldProviderConfig(devServerBootstrap?: LDDevServerBootstrap): ProviderConfig {
  const clientSideId = readEnv(process.env.LAUNCH_DARKLY_CLIENT_ID);

  // Flag keys in this app are snake_case (see flags.js), so auto-camel-casing
  // them would break every lookup. Shared by every branch: it is not a
  // per-branch preference and must not go missing from one of them.
  const reactOptions: ProviderConfig['reactOptions'] = { useCamelCaseFlagKeys: false };

  // FIRST, ahead of the client-side-ID check below, and that ordering is a
  // deliberate reversal of what this function used to do. The old rule - "no
  // real credential means this deployment was never evaluating flags against
  // anything, so do not invent a flag source" - was about refusing to DIAL
  // something nobody configured. A bootstrap is not a source to dial: it is a
  // flag set the server render already has in hand, for a Dev Server this stack
  // asked for by name. Falling back to hosted LaunchDarkly while holding it
  // would throw those values away and serve defaults instead.
  if (devServerBootstrap) {
    console.info(
      `${LOG_PREFIX} Bootstrapped from this stack's Dev Server (project ${devServerBootstrap.projectKey}) - ` +
        'the browser makes no LaunchDarkly requests, so flags are fixed for this page load.'
    );

    return {
      // The PROJECT KEY, not the client-side ID, and the substitution is
      // mandatory rather than incidental: LaunchDarkly's own dev-server guidance
      // is that a client-side ID makes the SDK connect to hosted LaunchDarkly
      // instead of the Dev Server. Here it is also what makes the credential
      // harmless - see this file's header - because nothing below leaves the
      // browser to spend it.
      clientSideID: devServerBootstrap.projectKey,
      options: {
        // The flag set `_document` fetched from the Dev Server, in the JS SDK's
        // `allFlagsState` bootstrap shape. Present in the FIRST render, on the
        // server and in the browser alike: `LDProvider`'s constructor seeds its
        // state from it (`provider.tsx`), which is what makes the server markup
        // and the hydrating client markup agree about every flag.
        bootstrap: devServerBootstrap.flags,
        // The four suppressions below are not optimisations. `bootstrap` alone
        // stops the SDK's initial `/sdk/evalx` POLL and nothing else; every
        // other request it makes is on by default and would go to HOSTED
        // LaunchDarkly under a project key that cannot authenticate there.
        //
        // `streaming: false` is the load-bearing one. `LDProvider` calls
        // `ldClient.on('change')` unconditionally, and `updateStreamingState`
        // opens an EventSource whenever `streaming` is left `undefined`
        // (js-sdk-common `index.js`) - so without this an SSE connection is
        // opened despite the bootstrap, and a failing stream retries forever.
        // Setting it also states the accepted trade-off in the one place the
        // SDK reads: on a Dev Server stack flags do not update live.
        streaming: false,
        // Analytics from a developer's stack are worth nothing and cannot be
        // delivered anyway - the Dev Server is unreachable from the browser and
        // hosted LaunchDarkly would reject the project key.
        sendEvents: false,
        // Experimentation goals, likewise: `fetchGoals` defaults to true and is
        // not gated on `bootstrap`, so this is the only thing that stops a
        // `/sdk/goals/{project}` request.
        fetchGoals: false,
        // And the SDK's own diagnostics, which post on a timer.
        diagnosticOptOut: true,
      },
      reactOptions,
    };
  }

  // The REAL credential. A deployment holding no client-side ID was never
  // evaluating flags against anything, and with no bootstrap in hand there is
  // nothing to evaluate them from either.
  if (!clientSideId) {
    // Every flag falls back to the default its call site passes, which looks
    // exactly like "all the flags are off" - with nothing tying that to the
    // missing client-side ID unless this line says so.
    console.warn(`${LOG_PREFIX} No client-side ID found - every flag will fall back to its default.`);

    // Unchanged from what this app has always done with an unset client-side ID,
    // down to the `"undefined"` string its template literal produced: the SDK
    // gets a non-empty credential that authenticates nothing, fails its fetches
    // and serves fallbacks. Passing `''` instead would make the SDK THROW on
    // initialization, turning a degraded app into a blank one.
    return { clientSideID: String(process.env.LAUNCH_DARKLY_CLIENT_ID), options: {}, reactOptions };
  }

  // The ordinary path: hosted LaunchDarkly, on the SDK's own default endpoints.
  // Production, staging and every stack without a Dev Server - and also a Dev
  // Server stack whose Dev Server could not be read, which degrades to exactly
  // this rather than to a browser holding a project key it cannot spend.
  return { clientSideID: clientSideId, options: {}, reactOptions };
}
